/**
 * The stat vocabulary the Play Entry form offers.
 *
 * Every list here mirrors a server contract and must not drift:
 *   - phases, play types, outcomes, specialty outcomes, hashes and the field
 *     bounds come from `services/plays/src/constants.ts`;
 *   - formations / personnel / coverages / fronts / motions / protections are
 *     the platform defaults seeded per tenant by
 *     `services/tenant/src/seeds/enum-seed.ts`.
 *
 * IMPORTANT — {@link DEFAULT_STAT_CATALOG} is a FALLBACK, not the source of
 * truth. Play Entry reads the tenant's live catalog from `GET /stat-schema/v1`
 * (`enum_categories`) via `useCustomStatFields`, because that is the same set
 * `services/plays` validates a submission against. A tenant may relabel a value
 * or retire one, and a tenant onboarded before a catalog migration may not have
 * the newest vocabulary yet.
 *
 * Shipping this list AS the catalog is what caused the 2026-09-04 Play Entry
 * failure: migration 0032 refreshed the stock lists, the UI picked them up on
 * deploy, but the per-tenant `enum_values` rows were never migrated — so the
 * form offered "Ace" and every save came back 422. Keep this in step with the
 * seed for the offline/unseeded case, and never reintroduce it as the primary
 * read path.
 */

export const PLAY_PHASES = ['offense', 'defense', 'special_teams'] as const;
export type PlayPhase = (typeof PLAY_PHASES)[number];

export const PHASE_LABELS: Record<PlayPhase, string> = {
  offense: 'Offense',
  defense: 'Defense',
  special_teams: 'Special Teams',
};

export const PHASE_SHORT_LABELS: Record<PlayPhase, string> = {
  offense: 'OFF',
  defense: 'DEF',
  special_teams: 'ST',
};

export function isPlayPhase(value: unknown): value is PlayPhase {
  return typeof value === 'string' && (PLAY_PHASES as readonly string[]).includes(value);
}

export interface CatalogOption {
  value: string;
  label: string;
  /**
   * Inner markup of a `viewBox="0 0 120 72"` SVG for the booth button
   * (formations), from `enum_values.glyph_svg`. The platform fallback below
   * carries none: artwork is server-owned, so an unseeded tenant gets text.
   */
  glyphSvg?: string | null;
}

export type StatCatalog = Record<
  'FORMATION' | 'COVERAGE' | 'FRONT' | 'PERSONNEL' | 'MOTION' | 'PROTECTION',
  readonly CatalogOption[]
>;

function options(entries: ReadonlyArray<[string, string]>): readonly CatalogOption[] {
  return entries.map(([value, label]) => ({ value, label }));
}

/**
 * Platform-default stat catalog, mirroring the LIVE half of the tenant seed
 * (`services/tenant/src/seeds/enum-seed.ts`) — keep the two in step. Only
 * used while `GET /stat-schema/v1` is in flight or failing.
 *
 * The lists follow the owner's stock-options sheet (migration 0044). Stock
 * codes dropped from it are deleted server-side, or retired when a logged play
 * still uses them, so they may appear on historical plays but are not offered
 * here for new entry.
 */
export const DEFAULT_STAT_CATALOG: StatCatalog = {
  FORMATION: options([
    ['Spread', 'Spread'],
    ['Trips', 'Trips'],
    ['Empty', 'Empty'],
    ['Pro-Spread', 'Pro Spread'],
    ['Pro-Trips', 'Pro Trips'],
    ['Trey', 'Trey'],
    ['Quads', 'Quads (4x1)'],
    ['Bunch', 'Bunch'],
    ['Ace', 'Ace'],
    ['Heavy', 'Heavy'],
    ['Stack', 'Stack'],
    ['Twins', 'Twins'],
    ['Trips-Bunch', 'Trips Bunch'],
    ['Empty-Bunch', 'Empty Bunch'],
    ['Unbalanced', 'Unbalanced'],
  ]),
  COVERAGE: options([
    ['Cover0', '0'],
    ['Cover1', '1'],
    ['Cover2', '2'],
    ['Cover3', '3'],
    ['Tampa2', 'Tampa 2'],
    ['Cover4', '4'],
    ['Cover6', '6'],
    ['2-Man', '2-Man'],
  ]),
  FRONT: options([
    ['3-3', '3-3'],
    ['3-4', '3-4'],
    ['4-4', '4-4'],
    ['4-3', '4-3'],
    ['4-2', '4-2'],
    ['5-2', '5-2'],
    ['Bear', 'Bear'],
  ]),
  PERSONNEL: options([
    ['10', '10 — 1 RB, 0 TE, 4 WR'],
    ['11', '11 — 1 RB, 1 TE, 3 WR'],
    ['12', '12 — 1 RB, 2 TE, 2 WR'],
    ['21', '21 — 2 RB, 1 TE, 2 WR'],
    ['22', '22 — 2 RB, 2 TE, 1 WR'],
    ['13', '13 — 1 RB, 3 TE, 1 WR'],
    ['20', '20 — 2 RB, 0 TE, 3 WR'],
    ['23', '23 — 2 RB, 3 TE, 0 WR'],
    ['32', '32 — 3 RB, 2 TE, 0 WR'],
    ['Heavy', 'Heavy'],
  ]),
  MOTION: options([
    ['Jet', 'Jet Motion'],
    ['Orbit', 'Orbit Motion'],
  ]),
  PROTECTION: options([
    ['Man', 'Man Protection'],
    ['Zone', 'Zone Protection'],
    ['Slide', 'Slide Protection'],
  ]),
};

/** Play types offered per phase (`PLAY_TYPES` in the plays service). */
export const PLAY_TYPES_BY_PHASE: Record<PlayPhase, readonly CatalogOption[]> = {
  offense: options([
    ['run', 'Run'],
    ['pass', 'Pass'],
    ['other', 'Other / No play'],
  ]),
  defense: options([
    ['run', 'Run (opponent)'],
    ['pass', 'Pass (opponent)'],
    ['other', 'Other / No play'],
  ]),
  special_teams: options([
    ['kickoff', 'Kickoff'],
    ['kick_return', 'Kick Return'],
    ['punt', 'Punt'],
    ['punt_return', 'Punt Return'],
    ['field_goal', 'Field Goal'],
    ['extra_point', 'Extra Point'],
    // The OPPONENT's kick with our block unit on the field: a make is THEIR points.
    ['fg_block', 'FG Block'],
    ['xp_block', 'XP Block'],
    ['other', 'Other / Two-point try'],
  ]),
};

/** Kicking plays where the opponent kicks and we block (special teams only). */
export const BLOCK_PLAY_TYPES: readonly string[] = ['fg_block', 'xp_block'];
/** Play types that carry a field-goal make / miss / block result. */
export const FIELD_GOAL_PLAY_TYPES: readonly string[] = ['field_goal', 'fg_block'];
/** Play types that carry an extra-point make / miss or a two-point try. */
export const EXTRA_POINT_PLAY_TYPES: readonly string[] = ['extra_point', 'xp_block'];

export const PLAY_OUTCOME_LABELS: Record<string, string> = {
  na: 'N/A (run or clean play)',
  complete: 'Complete',
  incomplete: 'Incomplete',
  drop: 'Drop',
  // Legacy rows only: Play Entry no longer offers it (penalties are tags now).
  penalty: 'Penalty',
  sack: 'Sack',
  tfl: 'Tackle for loss',
  no_play: 'No play',
};

/**
 * Outcomes still ACCEPTED but no longer OFFERED (`LEGACY_PLAY_OUTCOMES` in the
 * plays service). A penalty is now the Offensive / Defensive Penalty quick-tag
 * with the "No play" result, which replays the down.
 */
export const LEGACY_PLAY_OUTCOMES: readonly string[] = ['penalty'];

/** Outcomes Play Entry OFFERS per play type, mirroring `PLAY_OUTCOMES_BY_TYPE`. */
export const PLAY_OUTCOMES_BY_TYPE: Record<string, readonly string[]> = {
  run: ['na', 'tfl', 'no_play'],
  pass: ['na', 'complete', 'incomplete', 'drop', 'sack', 'no_play'],
  punt: ['na', 'no_play'],
  kickoff: ['na', 'no_play'],
  kick_return: ['na', 'tfl', 'no_play'],
  punt_return: ['na', 'tfl', 'no_play'],
  field_goal: ['na', 'no_play'],
  extra_point: ['na', 'no_play'],
  fg_block: ['na', 'no_play'],
  xp_block: ['na', 'no_play'],
  other: Object.keys(PLAY_OUTCOME_LABELS).filter((value) => !LEGACY_PLAY_OUTCOMES.includes(value)),
};

/** Whether the server still accepts `outcome` on `playType` (offered + legacy). */
export function isAcceptedOutcome(playType: string, outcome: string): boolean {
  return (
    LEGACY_PLAY_OUTCOMES.includes(outcome) ||
    (PLAY_OUTCOMES_BY_TYPE[playType] ?? PLAY_OUTCOMES_BY_TYPE.other ?? []).includes(outcome)
  );
}

export const SPECIALTY_OUTCOME_OPTIONS: readonly CatalogOption[] = options([
  ['touchdown', 'Touchdown'],
  // The team WITHOUT the ball scored: +6 for our defense on a DEF snap, for the
  // kicking team on a fumbled return, for the opponent on our OFF snap.
  ['defensive_touchdown', 'Defensive Touchdown'],
  ['interception', 'Interception'],
  // The snapping team LOST the ball (possession flips); mutually exclusive with
  // Fumble recovered (the snapping team kept it).
  ['fumble', 'Fumble (Turnover)'],
  ['fumble_recovered', 'Fumble recovered'],
  ['field_goal_make', 'Field goal made'],
  ['field_goal_miss', 'Field goal missed'],
  ['extra_point_make', 'Extra point made'],
  ['extra_point_miss', 'Extra point missed'],
  ['two_point_conversion', 'Two-point good'],
  ['two_point_failed', 'Two-point failed'],
  // The defending team returned the try: 2 points for them.
  ['defensive_two_point', 'Defensive two-point'],
  // Our punt blocked (Punt) or the opponent's punt we blocked (Punt Return).
  ['punt_block', 'Punt blocked'],
  // A field goal / extra point blocked, either side.
  ['kick_blocked', 'Kick blocked'],
  // The KICKING team recovered an onside kick.
  ['onside_recovered', 'Onside recovered'],
  ['safety', 'Safety'],
  ['touchback', 'Touchback'],
  ['turnover_on_downs', 'Turnover on downs'],
]);

/** Pairs that cannot both describe one snap (`ILLEGAL_SPECIALTY_PAIRS`). */
export const ILLEGAL_SPECIALTY_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ['touchdown', 'interception'],
  ['touchdown', 'safety'],
  ['field_goal_make', 'field_goal_miss'],
  ['extra_point_make', 'extra_point_miss'],
  ['touchdown', 'turnover_on_downs'],
  ['touchback', 'touchdown'],
  ['fumble', 'fumble_recovered'],
  ['touchdown', 'defensive_touchdown'],
  ['defensive_touchdown', 'safety'],
  ['defensive_touchdown', 'touchback'],
  ['touchdown', 'fumble'],
  ['defensive_touchdown', 'fumble_recovered'],
  ['two_point_conversion', 'two_point_failed'],
  ['two_point_conversion', 'defensive_two_point'],
  ['kick_blocked', 'field_goal_make'],
  ['kick_blocked', 'extra_point_make'],
  ['onside_recovered', 'touchback'],
];

/**
 * How the team without the ball got it (`DEFENSIVE_TD_CAUSES`): a Defensive
 * Touchdown must carry one of these. The cause is the turnover; the touchdown
 * is only the six points.
 */
export const DEFENSIVE_TD_CAUSES: readonly string[] = [
  'interception',
  'fumble',
  'punt_block',
  'kick_blocked',
  'field_goal_miss',
];

/** The validation message when a Defensive Touchdown has no cause. */
export const DEFENSIVE_TD_CAUSE_MESSAGE =
  'Defensive Touchdown needs how the ball changed hands: Interception, Fumble (Turnover), Punt blocked, Kick blocked or Field goal missed.';

/** Whether a Defensive Touchdown on these tags is missing its cause. */
export function isDefensiveTouchdownWithoutCause(specialty: readonly string[]): boolean {
  return (
    specialty.includes('defensive_touchdown') &&
    !DEFENSIVE_TD_CAUSES.some((cause) => specialty.includes(cause))
  );
}

/**
 * Chips that behave like radio buttons on the Tags card: tapping one clears
 * its partner, so the operator can never select both.
 */
export const EXCLUSIVE_SPECIALTY_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ['fumble', 'fumble_recovered'],
  ['field_goal_make', 'field_goal_miss'],
  ['extra_point_make', 'extra_point_miss'],
  ['touchdown', 'defensive_touchdown'],
  ['two_point_conversion', 'two_point_failed'],
];

/** Toggle one specialty chip, clearing its exclusive partner when selecting. */
export function toggleSpecialtyOutcome(
  selected: readonly string[],
  value: string
): readonly string[] {
  if (selected.includes(value)) return selected.filter((entry) => entry !== value);
  const partners = new Set(
    EXCLUSIVE_SPECIALTY_PAIRS.flatMap(([left, right]) =>
      left === value ? [right] : right === value ? [left] : []
    )
  );
  return [...selected.filter((entry) => !partners.has(entry)), value];
}

/**
 * Whether a Tags-card specialty chip applies to the current phase + play type
 * (report: "Tags should only be available on applicable plays"):
 * - FG made / missed: Special Teams + Field Goal or FG Block;
 * - XP made / missed: Special Teams + Extra Point or XP Block;
 * - Punt blocked: Special Teams + Punt / Punt Return;
 * - Kick blocked: Special Teams + Field Goal / Extra Point / FG Block / XP Block;
 * - Onside recovered: Special Teams + Kickoff / Kick Return;
 * - Two-point good / failed, Defensive two-point: Offense, or Special Teams +
 *   Extra Point / XP Block / Other (two-point try);
 * - Turnover on downs: Offense / Defense only.
 * Every other chip (TD, Defensive TD, INT, fumbles, safety, touchback) shows on
 * every play.
 */
export function isSpecialtyApplicable(value: string, phase: PlayPhase, playType: string): boolean {
  const isSpecialTeams = phase === 'special_teams';
  switch (value) {
    case 'field_goal_make':
    case 'field_goal_miss':
      return isSpecialTeams && FIELD_GOAL_PLAY_TYPES.includes(playType);
    case 'extra_point_make':
    case 'extra_point_miss':
      return isSpecialTeams && EXTRA_POINT_PLAY_TYPES.includes(playType);
    case 'punt_block':
      return isSpecialTeams && ['punt', 'punt_return'].includes(playType);
    case 'kick_blocked':
      return (
        isSpecialTeams && [...FIELD_GOAL_PLAY_TYPES, ...EXTRA_POINT_PLAY_TYPES].includes(playType)
      );
    case 'onside_recovered':
      return isSpecialTeams && ['kickoff', 'kick_return'].includes(playType);
    case 'two_point_conversion':
    case 'two_point_failed':
    case 'defensive_two_point':
      return (
        phase === 'offense' ||
        (isSpecialTeams && [...EXTRA_POINT_PLAY_TYPES, 'other'].includes(playType))
      );
    case 'turnover_on_downs':
      return !isSpecialTeams;
    default:
      return true;
  }
}

/** The specialty options that apply to this phase + play type, in catalog order. */
export function applicableSpecialtyOptions<T extends { value: string }>(
  options: readonly T[],
  phase: PlayPhase,
  playType: string
): readonly T[] {
  return options.filter((option) => isSpecialtyApplicable(option.value, phase, playType));
}

export const HASH_OPTIONS: readonly CatalogOption[] = options([
  ['left', 'Left hash'],
  ['middle', 'Middle'],
  ['right', 'Right hash'],
]);

/** Play types run from a line of scrimmage: down / gain are required (LoS stays optional). */
export const SCRIMMAGE_PLAY_TYPES: readonly string[] = ['run', 'pass'];

export const FIELD_BOUNDS = {
  quarterMax: 8,
  regulationQuarterMax: 4,
  clockMaxMinutes: 15,
  downMin: 1,
  downMax: 4,
  yardLineMin: 1,
  yardLineMax: 99,
  gainLossLimit: 99,
  kickDistanceMin: 0,
  kickDistanceMax: 99,
  returnYardsMin: -20,
  returnYardsMax: 109,
  notesMaxLength: 1000,
  tagsMaxCount: 20,
  /** Yards awarded for a new set of downs when the chains are reset. */
  yardsToGo: 10,
  /** The red zone starts at the opponent's 20: yard line `100 - 20` and up. */
  redZoneYardLine: 20,
} as const;

export const CLOCK_PATTERN = /^([0-9]{1,2}):([0-9]{2})$/;

export function defaultPlayType(phase: PlayPhase): string {
  return PLAY_TYPES_BY_PHASE[phase][0]?.value ?? 'other';
}

/**
 * Outcomes offered for the current play type, in display order. `current` keeps
 * a legacy outcome (e.g. `penalty` on an old play being edited) selectable.
 */
export function outcomeOptionsFor(playType: string, current?: string): readonly CatalogOption[] {
  const allowed = PLAY_OUTCOMES_BY_TYPE[playType] ?? PLAY_OUTCOMES_BY_TYPE.other ?? [];
  const values =
    current !== undefined && LEGACY_PLAY_OUTCOMES.includes(current) && !allowed.includes(current)
      ? [...allowed, current]
      : allowed;
  return values.map((value) => ({ value, label: PLAY_OUTCOME_LABELS[value] ?? value }));
}

export function isScrimmagePlay(playType: string): boolean {
  return SCRIMMAGE_PLAY_TYPES.includes(playType);
}
